// src/components/dashboard/jobs/JobDetails.jsx
import React, { useState, useMemo } from "react";
import "./JobDetails.css";

const JobDetails = ({ job, onClose }) => {
  // --- HOOKS CALLED AT THE TOP ---
  const [activeTab, setActiveTab] = useState("Description");
  const [imageError, setImageError] = useState(false);

  // Memoize derived data BEFORE the early return
  const displayQualifications = useMemo(() => {
    // Check if job and description/qualifications exist before accessing them
    if (!job || (!job.qualifications && !job.description)) return [];

    const qualifications = job.qualifications || [];
    const description = job.description || "";

    if (qualifications.length > 0) {
      return qualifications;
    }
    // Basic parsing from description as fallback
    return description
      .split("\n")
      .map((line) => line.trim())
      .filter(
        (line) =>
          line.startsWith("-") || line.startsWith("*") || line.match(/^\d+\./)
      )
      .map((line) => line.substring(line.indexOf(" ") + 1).trim())
      .filter(Boolean)
      .slice(0, 7);
  }, [job?.qualifications, job?.description]); // Depend on job properties

  const displayKeySkills = useMemo(() => {
    if (!job || !job.keySkills) return ["Relevant Skills"]; // Handle missing job or keyskills
    return job.keySkills.length > 0 ? job.keySkills : ["Relevant Skills"];
  }, [job?.keySkills]); // Depend on job property
  // --- END OF HOOKS ---

  // --- EARLY RETURN ---
  // If job is null or undefined after hooks, return null
  if (!job) return null;
  // --- END OF EARLY RETURN ---

  // Destructure the richer data from the transformed job object (now safe)
  const {
    companyName = "N/A",
    jobTitle = "N/A",
    jobType = "N/A",
    salary = "N/A",
    level = "N/A",
    location = "N/A",
    remote = "N/A",
    matchPercentage = 0,
    link = "#",
    description = "No detailed description available.",
    company_description = "",
    thumbnail = null,
    postedDate = null,
    // qualifications and keySkills are handled by useMemo above
  } = job;

  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
  };

  // --- Logo Generation ---
  const getLogoBackground = () => {
    if (!companyName || companyName === "N/A") return "#cccccc";
    const colors = [
      "#4285F4",
      "#EA4335",
      "#FBBC05",
      "#34A853",
      "#FF6D01",
      "#46BDC6",
      "#7E57C2",
      "#FF8A65",
    ];
    let hash = 0;
    for (let i = 0; i < companyName.length; i++) {
      hash = companyName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };
  const getLogoInitials = () => {
    if (!companyName || companyName === "N/A") return "?";
    const words = companyName.split(" ").filter(Boolean);
    return words.length > 1
      ? (words[0][0] + words[1][0]).toUpperCase()
      : companyName.charAt(0).toUpperCase();
  };
  // --- End Logo Generation ---

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
  };

  // Determine whether to show image or initials
  const showImage = thumbnail && !imageError;

  // --- JSX RENDER ---
  return (
    <div className="smai-job-details-modal">
      <div className="smai-job-details-overlay" onClick={onClose}></div>
      <div className="smai-job-details-content">
        {/* Header */}
        <div className="smai-job-details-header">
          <button
            className="smai-back-button"
            onClick={onClose}
            aria-label="Close job details"
          >
            <span>←</span>
          </button>
          <h2>Job Detail</h2>
          {/* --- MODIFIED: Changed button to a link for visiting the job page --- */}
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className={`smai-visit-page-button ${
              link === "#" ? "smai-disabled-link" : ""
            }`}
            aria-label="Visit job page"
            title="Visit job page"
            onClick={(e) => link === "#" && e.preventDefault()}
          >
            <span>↗</span>
          </a>
        </div>

        {/* Main Info Section */}
        <div className="smai-job-main-info">
          <div
            className="smai-company-logo-large"
            style={{
              backgroundColor: showImage ? "#f9fafb" : getLogoBackground(),
            }}
            aria-label={`${companyName} logo`}
          >
            {showImage ? (
              <img
                src={thumbnail}
                alt={`${companyName} logo`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  padding: "12px",
                }}
                onError={handleImageError}
              />
            ) : (
              <span className="smai-logo-text-large">{getLogoInitials()}</span>
            )}
          </div>
          <div className="smai-job-headline">
            <h3 className="smai-job-title-large">{jobTitle}</h3>
            <p className="smai-company-location">
              {companyName} • {location}
            </p>
          </div>
        </div>

        {/* Tags Section - Use real data */}
        <div className="smai-job-tags-large">
          {postedDate && (
            <span className="smai-job-tag-large">📅 {postedDate}</span>
          )}
          {jobType !== "N/A" && (
            <span className="smai-job-tag-large">{jobType}</span>
          )}
          {remote !== "N/A" && (
            <span className="smai-job-tag-large">{remote}</span>
          )}
          {level !== "N/A" && (
            <span className="smai-job-tag-large">{level}</span>
          )}
          {salary !== "N/A" && salary !== "Competitive" && (
            <span className="smai-job-tag-large">{salary}</span>
          )}
        </div>

        {/* Tabs Section */}
        <div className="smai-job-detail-tabs">
          <button
            className={
              activeTab === "Description" ? "smai-tab-active" : "smai-tab"
            }
            onClick={() => handleTabClick("Description")}
          >
            Description
          </button>
          <button
            className={activeTab === "Company" ? "smai-tab-active" : "smai-tab"}
            onClick={() => handleTabClick("Company")}
          >
            Company
          </button>
          {/* <button className={activeTab === 'Reviews' ? 'smai-tab-active' : 'smai-tab'} onClick={() => handleTabClick('Reviews')}>Reviews</button> */}
        </div>

        {/* Tab Content Area */}
        <div className="smai-job-detail-content-area">
          {activeTab === "Description" && (
            <div className="smai-tab-panel">
              <section className="smai-role-section">
                <h4>About this role</h4>
                <p className="smai-job-description">{description}</p>
              </section>

              <section className="smai-qualifications-section">
                <h4>Qualifications</h4>
                {displayQualifications.length > 0 ? (
                  <ul className="smai-qualifications-list">
                    {displayQualifications.map((qualification, index) => (
                      <li key={index}>{qualification}</li>
                    ))}
                  </ul>
                ) : (
                  <p>See description for qualification details.</p>
                )}
              </section>

              {/* Match Info Section */}
              <div className="smai-matching-info">
                <h4>Match Information</h4>
                <div className="smai-match-details">
                  {/* SVG Match Circle */}
                  <div className="smai-match-circle-large">
                    <svg
                      className="smai-match-progress"
                      width="100"
                      height="100"
                      viewBox="0 0 100 100"
                    >
                      <circle
                        className="smai-match-progress-bg"
                        cx="50"
                        cy="50"
                        r="45"
                      />
                      <circle
                        className="smai-match-progress-bar"
                        cx="50"
                        cy="50"
                        r="45"
                        style={{
                          strokeDasharray: `${2 * Math.PI * 45}`,
                          strokeDashoffset: `${
                            2 * Math.PI * 45 * (1 - matchPercentage / 100)
                          }`,
                        }}
                      />
                      <text
                        x="50"
                        y="50"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="smai-match-progress-text"
                      >
                        {Math.round(matchPercentage)}%
                      </text>
                    </svg>
                  </div>
                  <div className="smai-match-explanation">
                    <p>
                      Your profile shows a{" "}
                      {matchPercentage > 75
                        ? "strong"
                        : matchPercentage > 50
                        ? "good"
                        : "potential"}{" "}
                      match ({matchPercentage}%) for this position based on
                      analysis.
                    </p>
                    {displayKeySkills &&
                      displayKeySkills.length > 0 &&
                      displayKeySkills[0] !== "Relevant Skills" && (
                        <>
                          <p>Key Skills possibly related:</p>
                          <div className="smai-key-skills">
                            {displayKeySkills.map((skill, index) => (
                              <span key={index}>{skill}</span>
                            ))}
                          </div>
                        </>
                      )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === "Company" && (
            <div className="smai-tab-panel">
              <h4>About {companyName}</h4>
              <p className="smai-job-description">
                {company_description ||
                  `Information about ${companyName} is currently limited. This role offers an opportunity to join their team as a ${jobTitle}. Visit the company's website or the job posting for more detailed information about the company culture, values, and mission.`}
              </p>
              {/* Add some basic info that we do have */}
              <div style={{ marginTop: "20px" }}>
                <h5>Job Details:</h5>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  <li>
                    <strong>Location:</strong> {location}
                  </li>
                  <li>
                    <strong>Job Type:</strong> {jobType}
                  </li>
                  {salary !== "N/A" && salary !== "Competitive" && (
                    <li>
                      <strong>Salary:</strong> {salary}
                    </li>
                  )}
                  {postedDate && (
                    <li>
                      <strong>Posted:</strong> {postedDate}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
          {/* Add Reviews tab content rendering here if needed */}
        </div>

        {/* Actions Section */}
        <div className="smai-job-actions">
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className={`smai-apply-button ${
              link === "#" ? "smai-disabled-link" : ""
            }`}
            onClick={(e) => link === "#" && e.preventDefault()}
          >
            {link === "#" ? "Apply Link Unavailable" : "Apply Now"}
          </a>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
